import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';

const CartContext = createContext();

// Начальное состояние корзины
const initialState = {
  items: [],
  totalItems: 0,
  totalAmount: 0,
  isLoading: false,
};

const normalizeCurrencyCode = (currencyCode) => {
  const code = String(currencyCode || 'RUB').toUpperCase();
  if (['RUB', 'EUR', 'USD'].includes(code)) return code;
  return 'RUB';
};

const buildCartItemKey = (item = {}) => {
  if (item.cartKey) return String(item.cartKey);
  const type = item.type || 'product';
  const id = item.id ?? 'unknown';
  const currency = normalizeCurrencyCode(item.currency);
  const bookType = item.bookType || '';
  const digitalEmail = item.digitalEmail || '';
  return [type, id, currency, bookType, digitalEmail].join(':');
};

const normalizeCartItem = (item = {}) => {
  const quantity = Number(item.quantity) > 0 ? Number(item.quantity) : 1;
  const price = Number(item.price) || 0;
  const currency = normalizeCurrencyCode(item.currency);
  const normalized = {
    ...item,
    quantity,
    price,
    currency
  };
  normalized.cartKey = buildCartItemKey(normalized);
  return normalized;
};

const calculateCartTotals = (items = []) => {
  const totalItems = items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  const totalAmount = items.reduce((sum, item) => {
    const price = Number(item.price) || 0;
    const quantity = Number(item.quantity) || 0;
    return sum + (price * quantity);
  }, 0);

  return { totalItems, totalAmount };
};

// Reducer для управления корзиной
const cartReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'LOAD_CART':
      {
        const normalizedItems = (action.payload.items || []).map((item) => normalizeCartItem(item));
        const totals = calculateCartTotals(normalizedItems);
      return {
        ...state,
          items: normalizedItems,
          totalItems: totals.totalItems,
          totalAmount: totals.totalAmount,
      };
      }
    
    case 'ADD_ITEM': {
      const { item, quantity = 1 } = action.payload;
      const normalizedItem = normalizeCartItem(item);
      const itemKey = buildCartItemKey(normalizedItem);
      const existingItemIndex = state.items.findIndex((cartItem) => buildCartItemKey(cartItem) === itemKey);
      
      let newItems;
      if (existingItemIndex >= 0) {
        // Обновляем количество существующего товара
        newItems = state.items.map((cartItem, index) =>
          index === existingItemIndex
            ? { ...cartItem, quantity: cartItem.quantity + quantity }
            : cartItem
        );
      } else {
        // Добавляем новый товар
        newItems = [...state.items, { ...normalizedItem, quantity }];
      }

      const totals = calculateCartTotals(newItems);
      
      const newState = {
        ...state,
        items: newItems,
        totalItems: totals.totalItems,
        totalAmount: totals.totalAmount,
      };
      
      return newState;
    }
    
    case 'UPDATE_QUANTITY': {
      const { id, type, currency, cartKey, quantity } = action.payload;
      
      if (quantity <= 0) {
        return cartReducer(state, { type: 'REMOVE_ITEM', payload: { id, type, currency, cartKey } });
      }
      
      const normalizedCurrency = normalizeCurrencyCode(currency);
      const newItems = state.items.map((item) => {
        const matchesByKey = cartKey && buildCartItemKey(item) === String(cartKey);
        const matchesLegacy = !cartKey && item.id === id && item.type === type && normalizeCurrencyCode(item.currency) === normalizedCurrency;
        return matchesByKey || matchesLegacy ? { ...item, quantity } : item;
      });

      const totals = calculateCartTotals(newItems);
      
      return {
        ...state,
        items: newItems,
        totalItems: totals.totalItems,
        totalAmount: totals.totalAmount,
      };
    }
    
    case 'REMOVE_ITEM': {
      const { id, type, currency, cartKey } = action.payload;
      const normalizedCurrency = normalizeCurrencyCode(currency);
      const newItems = state.items.filter(
        (item) => {
          const matchesByKey = cartKey && buildCartItemKey(item) === String(cartKey);
          const matchesLegacy = !cartKey && item.id === id && item.type === type && normalizeCurrencyCode(item.currency) === normalizedCurrency;
          return !(matchesByKey || matchesLegacy);
        }
      );

      const totals = calculateCartTotals(newItems);
      
      return {
        ...state,
        items: newItems,
        totalItems: totals.totalItems,
        totalAmount: totals.totalAmount,
      };
    }
    
    case 'CLEAR_CART':
      return {
        ...initialState,
        isLoading: false,
      };
    
    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const { user, isAuthenticated } = useAuth();
  const { t } = useTranslation();

  // Функция для получения ключа корзины
  const getCartKey = () => {
    return user?.id ? `cart_${user.id}` : 'cart_guest';
  };

  // Загрузка корзины из localStorage при инициализации или смене пользователя
  useEffect(() => {
    const cartKey = getCartKey();
    const savedCart = localStorage.getItem(cartKey);
    
    if (savedCart) {
      try {
        const cartData = JSON.parse(savedCart);
        dispatch({ type: 'LOAD_CART', payload: cartData });
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
        localStorage.removeItem(cartKey);
      }
    } else {
      // Очищаем корзину если нет сохраненных данных для этого пользователя
      dispatch({ type: 'CLEAR_CART' });
    }
  }, [user?.id]);

  // Сохранение корзины в localStorage при изменениях
  useEffect(() => {
    if (user?.id) { // Сохраняем только для авторизованных пользователей
      const cartData = {
        items: state.items,
        totalItems: state.totalItems,
        totalAmount: state.totalAmount,
      };
      localStorage.setItem(getCartKey(), JSON.stringify(cartData));
    }
  }, [state.items, state.totalItems, state.totalAmount, user?.id]);

  // Добавление товара в корзину
  const addToCart = (item, quantity = 1) => {
    if (!isAuthenticated) {
      toast.error(t('shop.login_required'));
      return false;
    }

    dispatch({
      type: 'ADD_ITEM',
      payload: { item, quantity },
    });
    
    toast.success(t('shop.added_to_cart', { name: item.name }));
    return true;
  };

  // Обновление количества товара
  const updateQuantity = (id, type, quantity, currency, cartKey) => {
    if (!isAuthenticated) {
      toast.error(t('shop.login_required'));
      return false;
    }

    dispatch({
      type: 'UPDATE_QUANTITY',
      payload: { id, type, quantity, currency, cartKey },
    });
    return true;
  };

  // Удаление товара из корзины
  const removeFromCart = (id, type, currency, cartKey) => {
    if (!isAuthenticated) {
      toast.error(t('shop.login_required'));
      return false;
    }

    dispatch({
      type: 'REMOVE_ITEM',
      payload: { id, type, currency, cartKey },
    });
    
    toast.success('Товар удалён из корзины');
  };

  // Очистка корзины
  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
    toast.success('Корзина очищена');
  };

  // Получение товара из корзины
  const getCartItem = (id, type, currency, cartKey) => {
    const normalizedCurrency = normalizeCurrencyCode(currency);
    return state.items.find((item) => {
      if (cartKey) return buildCartItemKey(item) === String(cartKey);
      return item.id === id && item.type === type && normalizeCurrencyCode(item.currency) === normalizedCurrency;
    });
  };

  // Проверка наличия товара в корзине
  const isInCart = (id, type, currency, cartKey) => {
    return !!getCartItem(id, type, currency, cartKey);
  };

  // Получение количества конкретного товара
  const getItemQuantity = (id, type, currency, cartKey) => {
    const item = getCartItem(id, type, currency, cartKey);
    return item ? item.quantity : 0;
  };

  // Валидация корзины (проверка наличия товаров)
  const validateCart = async () => {
    // Здесь можно добавить проверку наличия товаров на сервере
    // и обновить цены, если они изменились
    return true;
  };

  const value = {
    ...state,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    getCartItem,
    isInCart,
    getItemQuantity,
    validateCart,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export default CartContext;
